import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { attendanceAPI } from '../../../services';
import { useAsyncAction } from '../../../hooks';
import {
  Button,
  LoadingSpinner,
  Badge,
  ErrorMessage,
  SuccessMessage,
  Modal,
} from '../../UI';
import { ClipboardList, CheckCircle2, XCircle, AlertTriangle, Clock, ChevronLeft, ChevronRight, User, Calendar, CheckSquare, Square } from 'lucide-react';

const AdminRegularizationPage = () => {
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  
  // Pagination states for each tab
  const [pendingPage, setPendingPage] = useState(1);
  const [approvedPage, setApprovedPage] = useState(1);
  const [rejectedPage, setRejectedPage] = useState(1);
  const [lopPage, setLopPage] = useState(1);
  
  // Data states for each tab
  const [pendingData, setPendingData] = useState({ regularizations: [], pagination: null });
  const [approvedData, setApprovedData] = useState({ regularizations: [], pagination: null });
  const [rejectedData, setRejectedData] = useState({ regularizations: [], pagination: null });
  const [lopData, setLopData] = useState({ regularizations: [], pagination: null });
  
  // Loading states
  const [pendingLoading, setPendingLoading] = useState(false);
  const [approvedLoading, setApprovedLoading] = useState(false);
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [lopLoading, setLopLoading] = useState(false);
  
  // Action modal states
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [comments, setComments] = useState('');
  const [markAsLOP, setMarkAsLOP] = useState(true);
  const [approvedStatus, setApprovedStatus] = useState('present');

  // Bulk selection states
  const [selectedRegularizations, setSelectedRegularizations] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkComments, setBulkComments] = useState('');
  const [bulkApprovedStatus, setBulkApprovedStatus] = useState('present');
  const [bulkMarkAsLOP, setBulkMarkAsLOP] = useState(true);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const { loading: actionLoading, execute: executeAction } = useAsyncAction();

  const ITEMS_PER_PAGE = 20;

  // Fetch functions
  const fetchPending = useCallback(async (page = 1) => {
    setPendingLoading(true);
    try {
      const response = await attendanceAPI.getAllRegularizations({ 
        status: 'pending', 
        page, 
        limit: ITEMS_PER_PAGE 
      });
      setPendingData({
        regularizations: response.data?.regularizations || [],
        pagination: response.data?.pagination || null
      });
    } catch (err) {
      console.error('Error fetching pending:', err);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const fetchApproved = useCallback(async (page = 1) => {
    setApprovedLoading(true);
    try {
      const response = await attendanceAPI.getAllRegularizations({ 
        status: 'approved', 
        page, 
        limit: ITEMS_PER_PAGE 
      });
      setApprovedData({
        regularizations: response.data?.regularizations || [],
        pagination: response.data?.pagination || null
      });
    } catch (err) {
      console.error('Error fetching approved:', err);
    } finally {
      setApprovedLoading(false);
    }
  }, []);

  const fetchRejected = useCallback(async (page = 1) => {
    setRejectedLoading(true);
    try {
      const response = await attendanceAPI.getAllRegularizations({ 
        status: 'rejected', 
        page, 
        limit: ITEMS_PER_PAGE 
      });
      setRejectedData({
        regularizations: response.data?.regularizations || [],
        pagination: response.data?.pagination || null
      });
    } catch (err) {
      console.error('Error fetching rejected:', err);
    } finally {
      setRejectedLoading(false);
    }
  }, []);

  const fetchLop = useCallback(async (page = 1) => {
    setLopLoading(true);
    try {
      const response = await attendanceAPI.getAllRegularizations({ 
        status: 'lop', 
        page, 
        limit: ITEMS_PER_PAGE 
      });
      setLopData({
        regularizations: response.data?.regularizations || [],
        pagination: response.data?.pagination || null
      });
    } catch (err) {
      console.error('Error fetching lop:', err);
    } finally {
      setLopLoading(false);
    }
  }, []);

  // Initial fetch ALL tabs on mount to show correct counts
  useEffect(() => {
    fetchPending(1);
    fetchApproved(1);
    fetchRejected(1);
    fetchLop(1);
  }, [fetchPending, fetchApproved, fetchRejected, fetchLop]);

  // Fetch data when tab changes or page changes
  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPending(pendingPage);
    } else if (activeTab === 'approved') {
      fetchApproved(approvedPage);
    } else if (activeTab === 'rejected') {
      fetchRejected(rejectedPage);
    } else if (activeTab === 'lop') {
      fetchLop(lopPage);
    }
  }, [activeTab, pendingPage, approvedPage, rejectedPage, lopPage, fetchPending, fetchApproved, fetchRejected, fetchLop]);

  // Refetch all tabs
  const refetchAll = () => {
    fetchPending(pendingPage);
    if (activeTab === 'approved') fetchApproved(approvedPage);
    if (activeTab === 'rejected') fetchRejected(rejectedPage);
    if (activeTab === 'lop') fetchLop(lopPage);
  };

  const handleAction = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setComments('');
    setMarkAsLOP(true);
    setApprovedStatus(request.requestedStatus || 'present');
    setShowActionModal(true);
  };

  const confirmAction = async () => {
    try {
      setError('');
      
      let result;
      if (actionType === 'approve') {
        result = await executeAction(
          attendanceAPI.approveRegularization,
          selectedRequest._id,
          { comments, approvedStatus }
        );
      } else {
        result = await executeAction(
          attendanceAPI.rejectRegularization,
          selectedRequest._id,
          { comments, markAsLOP }
        );
      }

      if (result.success) {
        const actionText = actionType === 'approve' ? 'approved' : 'rejected';
        setSuccess('Regularization ' + actionText + ' successfully!');
        setShowActionModal(false);
        refetchAll();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Failed to ' + actionType + ' regularization');
      }
    } catch (err) {
      setError(err.message || 'Action failed');
    }
  };

  // Bulk selection handlers
  const toggleSelectRegularization = (regId) => {
    setSelectedRegularizations(prev => 
      prev.includes(regId) 
        ? prev.filter(id => id !== regId)
        : [...prev, regId]
    );
  };

  const toggleSelectAll = () => {
    const currentRequests = pendingData.regularizations;
    if (selectedRegularizations.length === currentRequests.length) {
      setSelectedRegularizations([]);
    } else {
      setSelectedRegularizations(currentRequests.map(r => r._id));
    }
  };

  const openBulkModal = (action) => {
    if (selectedRegularizations.length === 0) {
      setError('Please select at least one regularization request');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setBulkAction(action);
    setBulkComments('');
    setBulkApprovedStatus('present');
    setBulkMarkAsLOP(true);
    setShowBulkModal(true);
  };

  const handleBulkAction = async () => {
    if (bulkAction === 'reject' && !bulkComments.trim()) {
      setError('Please provide a reason for bulk rejection');
      return;
    }

    setBulkProcessing(true);
    try {
      let response;
      if (bulkAction === 'approve') {
        response = await attendanceAPI.bulkApproveRegularizations(selectedRegularizations, {
          comments: bulkComments,
          approvedStatus: bulkApprovedStatus
        });
      } else {
        response = await attendanceAPI.bulkRejectRegularizations(selectedRegularizations, {
          comments: bulkComments,
          markAsLOP: bulkMarkAsLOP
        });
      }

      const successCount = response.data?.success?.length || 0;
      const failedCount = response.data?.failed?.length || 0;

      if (successCount > 0) {
        let msg = 'Bulk ' + bulkAction + ': ' + successCount + ' successful';
        if (failedCount > 0) {
          msg += ', ' + failedCount + ' failed';
        }
        setSuccess(msg);
      } else if (failedCount > 0) {
        setError('Bulk ' + bulkAction + ' failed for all ' + failedCount + ' items');
      }

      setShowBulkModal(false);
      setSelectedRegularizations([]);
      refetchAll();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Bulk ' + bulkAction + ' failed');
    } finally {
      setBulkProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'warning', label: 'Pending' },
      approved: { variant: 'success', label: 'Approved' },
      rejected: { variant: 'danger', label: 'Rejected' },
      lop: { variant: 'danger', label: 'LOP' }
    };
    const config = statusConfig[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getTypeLabel = (type) => {
    const typeLabels = {
      forgot_punch: 'Forgot to Punch',
      missing_punch_in: 'Missing Punch In',
      missing_punch_out: 'Missing Punch Out',
      wrong_location: 'Wrong Location',
      system_error: 'System Error',
      other: 'Other'
    };
    return typeLabels[type] || type;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (date) => {
    if (!date) return '--:--';
    return new Date(date).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  // Pagination component
  const Pagination = ({ pagination, currentPage, onPageChange }) => {
    if (!pagination || pagination.pages <= 1) return null;
    
    return (
      <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6 mt-4">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === pagination.pages}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{((currentPage - 1) * ITEMS_PER_PAGE) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, pagination.total)}</span> of{' '}
              <span className="font-medium">{pagination.total}</span> results
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              {[...Array(Math.min(5, pagination.pages))].map((_, idx) => {
                let pageNum;
                if (pagination.pages <= 5) {
                  pageNum = idx + 1;
                } else if (currentPage <= 3) {
                  pageNum = idx + 1;
                } else if (currentPage >= pagination.pages - 2) {
                  pageNum = pagination.pages - 4 + idx;
                } else {
                  pageNum = currentPage - 2 + idx;
                }
                
                const isActive = pageNum === currentPage;
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={
                      'relative inline-flex items-center px-4 py-2 text-sm font-semibold ' +
                      (isActive
                        ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                        : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0')
                    }
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === pagination.pages}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  const renderRequestsTable = (requests, loading, showActions = false, pagination = null, currentPage = 1, onPageChange = () => {}) => {
    if (loading) {
      return (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      );
    }

    if (requests.length === 0) {
      return (
        <div className="text-center py-12">
          <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No regularization requests found</p>
        </div>
      );
    }

    return (
      <>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {showActions && (
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={toggleSelectAll}
                      className="p-1 hover:bg-gray-200 rounded"
                      title={selectedRegularizations.length === requests.length ? 'Deselect All' : 'Select All'}
                    >
                      {selectedRegularizations.length === requests.length && requests.length > 0 ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested As</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                {!showActions && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                )}
                {showActions && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {requests.map((reg) => {
                const isSelected = selectedRegularizations.includes(reg._id);
                return (
                  <tr key={reg._id} className={'hover:bg-gray-50 ' + (isSelected ? 'bg-blue-50' : '')}>
                    {showActions && (
                      <td className="px-4 py-4">
                        <button
                          onClick={() => toggleSelectRegularization(reg._id)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          {isSelected ? (
                            <CheckSquare className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      </td>
                    )}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{reg.employee?.name || 'N/A'}</p>
                          <p className="text-xs text-gray-500">{reg.employee?.employeeId || ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {formatDate(reg.date)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {getTypeLabel(reg.type)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex flex-col">
                        {reg.requestedPunchIn && (
                          <span className="text-green-600">In: {formatTime(reg.requestedPunchIn)}</span>
                        )}
                        {reg.requestedPunchOut && (
                          <span className="text-red-600">Out: {formatTime(reg.requestedPunchOut)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <span className={
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ' +
                        (reg.requestedStatus === 'half_day' 
                          ? 'bg-yellow-100 text-yellow-700' 
                          : 'bg-green-100 text-green-700')
                      }>
                        {reg.requestedStatus === 'half_day' ? 'Half Day' : 'Full Day'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600 max-w-xs">
                      <p className="truncate" title={reg.reason}>{reg.reason}</p>
                    </td>
                    {!showActions && (
                      <td className="px-4 py-4 whitespace-nowrap">
                        {getStatusBadge(reg.status)}
                        {reg.isLOP && reg.lopDeduction > 0 && (
                          <span className="ml-2 text-xs text-red-600 font-medium">
                            -{'\u20B9'}{reg.lopDeduction?.toLocaleString()}
                          </span>
                        )}
                      </td>
                    )}
                    {showActions && (
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAction(reg, 'approve')}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Approve"
                          >
                            <CheckCircle2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleAction(reg, 'reject')}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Reject (Mark as LOP)"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination pagination={pagination} currentPage={currentPage} onPageChange={onPageChange} />
      </>
    );
  };

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      <div>
        <h2 className="text-xl font-bold text-gray-900">Attendance Regularization</h2>
        <p className="text-gray-500">Review and manage employee attendance regularization requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div 
          className={'bg-white rounded-xl border-2 p-4 cursor-pointer transition-all ' + (activeTab === 'pending' ? 'border-yellow-500 shadow-md' : 'border-gray-200 hover:border-gray-300')}
          onClick={() => setActiveTab('pending')}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{pendingData.pagination?.total || 0}</p>
            </div>
          </div>
        </div>
        <div 
          className={'bg-white rounded-xl border-2 p-4 cursor-pointer transition-all ' + (activeTab === 'approved' ? 'border-green-500 shadow-md' : 'border-gray-200 hover:border-gray-300')}
          onClick={() => setActiveTab('approved')}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Approved</p>
              <p className="text-2xl font-bold text-green-600">{approvedData.pagination?.total || 0}</p>
            </div>
          </div>
        </div>
        <div 
          className={'bg-white rounded-xl border-2 p-4 cursor-pointer transition-all ' + (activeTab === 'rejected' ? 'border-red-500 shadow-md' : 'border-gray-200 hover:border-gray-300')}
          onClick={() => setActiveTab('rejected')}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Rejected</p>
              <p className="text-2xl font-bold text-red-600">{rejectedData.pagination?.total || 0}</p>
            </div>
          </div>
        </div>
        <div 
          className={'bg-white rounded-xl border-2 p-4 cursor-pointer transition-all ' + (activeTab === 'lop' ? 'border-orange-500 shadow-md' : 'border-gray-200 hover:border-gray-300')}
          onClick={() => setActiveTab('lop')}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">LOP</p>
              <p className="text-2xl font-bold text-orange-600">{lopData.pagination?.total || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('pending')}
              className={'px-6 py-4 text-sm font-medium border-b-2 transition-colors ' + 
                (activeTab === 'pending'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700')
              }
            >
              Pending ({pendingData.pagination?.total || 0})
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={'px-6 py-4 text-sm font-medium border-b-2 transition-colors ' +
                (activeTab === 'approved'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700')
              }
            >
              Approved ({approvedData.pagination?.total || 0})
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={'px-6 py-4 text-sm font-medium border-b-2 transition-colors ' +
                (activeTab === 'rejected'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700')
              }
            >
              Rejected ({rejectedData.pagination?.total || 0})
            </button>
            <button
              onClick={() => setActiveTab('lop')}
              className={'px-6 py-4 text-sm font-medium border-b-2 transition-colors ' +
                (activeTab === 'lop'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700')
              }
            >
              LOP ({lopData.pagination?.total || 0})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'pending' && (
            <>
              {/* Bulk Action Buttons */}
              {selectedRegularizations.length > 0 && (
                <div className="flex items-center gap-4 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <span className="text-sm font-medium text-blue-700">
                    {selectedRegularizations.length} selected
                  </span>
                  <div className="flex-1" />
                  <Button
                    onClick={() => openBulkModal('approve')}
                    className="bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-2"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5 inline" />
                    Bulk Approve
                  </Button>
                  <Button
                    onClick={() => openBulkModal('reject')}
                    className="bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2"
                  >
                    <XCircle className="w-4 h-4 mr-1.5 inline" />
                    Bulk Reject
                  </Button>
                  <Button
                    onClick={() => setSelectedRegularizations([])}
                    variant="outline"
                    className="text-sm px-4 py-2"
                  >
                    Clear Selection
                  </Button>
                </div>
              )}
              {renderRequestsTable(
                pendingData.regularizations, 
                pendingLoading, 
                true, 
                pendingData.pagination, 
                pendingPage, 
                setPendingPage
              )}
            </>
          )}

          {activeTab === 'approved' && (
            renderRequestsTable(
              approvedData.regularizations, 
              approvedLoading, 
              false, 
              approvedData.pagination, 
              approvedPage, 
              setApprovedPage
            )
          )}

          {activeTab === 'rejected' && (
            renderRequestsTable(
              rejectedData.regularizations, 
              rejectedLoading, 
              false, 
              rejectedData.pagination, 
              rejectedPage, 
              setRejectedPage
            )
          )}

          {activeTab === 'lop' && (
            renderRequestsTable(
              lopData.regularizations, 
              lopLoading, 
              false, 
              lopData.pagination, 
              lopPage, 
              setLopPage
            )
          )}
        </div>
      </div>

      {/* Action Modal */}
      <Modal
        isOpen={showActionModal}
        onClose={() => setShowActionModal(false)}
        title={actionType === 'approve' ? 'Approve Regularization' : 'Reject Regularization'}
        size="md"
      >
        {selectedRequest && (
          <div className="space-y-4">
            {/* Request Details */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Employee:</span>
                <span className="text-sm font-medium">{selectedRequest.employee?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Date:</span>
                <span className="text-sm font-medium">{formatDate(selectedRequest.date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Type:</span>
                <span className="text-sm font-medium">{getTypeLabel(selectedRequest.type)}</span>
              </div>
              {selectedRequest.requestedPunchIn && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Requested Punch In:</span>
                  <span className="text-sm font-medium text-green-600">{formatTime(selectedRequest.requestedPunchIn)}</span>
                </div>
              )}
              {selectedRequest.requestedPunchOut && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Requested Punch Out:</span>
                  <span className="text-sm font-medium text-red-600">{formatTime(selectedRequest.requestedPunchOut)}</span>
                </div>
              )}
              <div className="pt-2 border-t">
                <span className="text-sm text-gray-500">Reason:</span>
                <p className="text-sm mt-1">{selectedRequest.reason}</p>
              </div>
            </div>

            {actionType === 'approve' ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <CheckCircle2 className="w-4 h-4 inline-block mr-1" />
                    The attendance record will be updated with the requested punch times.
                  </p>
                </div>

                {/* Attendance Status Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mark Attendance As
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setApprovedStatus('present')}
                      className={'flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ' +
                        (approvedStatus === 'present'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300')
                      }
                    >
                      <CheckCircle2 className="w-4 h-4 inline-block mr-1.5" />
                      Full Day Present
                    </button>
                    <button
                      type="button"
                      onClick={() => setApprovedStatus('half_day')}
                      className={'flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ' +
                        (approvedStatus === 'half_day'
                          ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300')
                      }
                    >
                      <Clock className="w-4 h-4 inline-block mr-1.5" />
                      Half Day
                    </button>
                  </div>
                  {selectedRequest.requestedStatus && (
                    <p className="text-xs text-gray-500 mt-1">
                      Employee requested: {selectedRequest.requestedStatus === 'half_day' ? 'Half Day' : 'Full Day'}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">
                    <AlertTriangle className="w-4 h-4 inline-block mr-1" />
                    Rejecting this request will mark the day as absent.
                  </p>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="markAsLOP"
                    checked={markAsLOP}
                    onChange={(e) => setMarkAsLOP(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="markAsLOP" className="text-sm text-gray-700">
                    Mark as Loss of Pay (LOP) and deduct salary
                  </label>
                </div>
              </div>
            )}

            {/* Comments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comments (Optional)
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                placeholder="Add any comments for the employee..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowActionModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmAction}
                loading={actionLoading}
                className={actionType === 'approve' 
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
                }
              >
                {actionType === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Action Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        title={bulkAction === 'approve' ? 'Bulk Approve Regularizations' : 'Bulk Reject Regularizations'}
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              You are about to <span className="font-semibold">{bulkAction}</span>{' '}
              <span className="font-semibold text-blue-600">{selectedRegularizations.length}</span> regularization request(s).
            </p>
          </div>

          {bulkAction === 'approve' ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <CheckCircle2 className="w-4 h-4 inline-block mr-1" />
                  All selected attendance records will be updated with the requested punch times.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mark All Attendance As
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setBulkApprovedStatus('present')}
                    className={'flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ' +
                      (bulkApprovedStatus === 'present'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300')
                    }
                  >
                    <CheckCircle2 className="w-4 h-4 inline-block mr-1.5" />
                    Full Day Present
                  </button>
                  <button
                    type="button"
                    onClick={() => setBulkApprovedStatus('half_day')}
                    className={'flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ' +
                      (bulkApprovedStatus === 'half_day'
                        ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300')
                    }
                  >
                    <Clock className="w-4 h-4 inline-block mr-1.5" />
                    Half Day
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <AlertTriangle className="w-4 h-4 inline-block mr-1" />
                  Rejecting these requests will mark the days as absent.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="bulkMarkAsLOP"
                  checked={bulkMarkAsLOP}
                  onChange={(e) => setBulkMarkAsLOP(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="bulkMarkAsLOP" className="text-sm text-gray-700">
                  Mark as Loss of Pay (LOP) and deduct salary
                </label>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comments {bulkAction === 'reject' ? '(Required)' : '(Optional)'}
            </label>
            <textarea
              value={bulkComments}
              onChange={(e) => setBulkComments(e.target.value)}
              rows={3}
              placeholder={bulkAction === 'approve' 
                ? 'Add any comments for all employees...' 
                : 'Please provide a reason for rejection...'
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowBulkModal(false)}
              disabled={bulkProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkAction}
              loading={bulkProcessing}
              className={bulkAction === 'approve' 
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
              }
            >
              {bulkAction === 'approve' 
                ? 'Approve ' + selectedRegularizations.length + ' Request(s)' 
                : 'Reject ' + selectedRegularizations.length + ' Request(s)'
              }
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminRegularizationPage;
